import { Fingerprint, X } from 'lucide-react';

import { useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { isPasskeySupported } from '@splitfiesta/platform-web/auth/cognito';
import { Button } from '@splitfiesta/ui';

import { Portal } from '@/components/ui/Portal';

import { useRegisterPasskeyMutation } from '@/hooks/queries/usePasskeys';

import { useAuthStore } from '@/stores/authStore';

interface PasskeyPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modal prompt to encourage users to register a passkey after login/registration.
 * Only shows if browser supports passkeys.
 */
export function PasskeyPrompt({
  isOpen,
  onClose,
  onSuccess,
}: PasskeyPromptProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  // Get user email for passkey registration (for usernameless sign-in)
  const userEmail = useAuthStore(state => state.user?.email);
  const registerMutation = useRegisterPasskeyMutation(userEmail);

  const handleRegister = async () => {
    setError(null);
    try {
      await registerMutation.mutateAsync();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('settings:passkeys.registerError')
      );
    }
  };

  const handleSkip = () => {
    // Store that user dismissed the prompt so we don't show it again this session
    sessionStorage.setItem('passkey_prompt_dismissed', 'true');
    onClose();
  };

  // Don't render if passkeys not supported or modal is closed
  if (!isOpen || !isPasskeySupported()) {
    return null;
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={handleSkip}
          data-testid="passkey-prompt-backdrop"
        />

        {/* Modal Content */}
        <div
          className="relative z-50 w-full max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-xl"
          data-testid="passkey-prompt-modal"
        >
          <div className="p-6">
            {/* Close Button */}
            <button
              type="button"
              onClick={handleSkip}
              aria-label={t('common:actions.close')}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              data-testid="passkey-prompt-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Fingerprint className="w-8 h-8 text-orange-500" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-2">
              {t('auth:passkey.promptTitle')}
            </h2>

            {/* Description */}
            <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-6">
              {t('auth:passkey.promptDescription')}
            </p>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {error}
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleRegister}
                disabled={registerMutation.isPending}
                className="w-full"
                data-testid="passkey-prompt-register"
              >
                {registerMutation.isPending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                ) : (
                  <Fingerprint className="w-5 h-5 mr-2" />
                )}
                {t('auth:passkey.addPasskey')}
              </Button>

              <button
                type="button"
                onClick={handleSkip}
                disabled={registerMutation.isPending}
                className="w-full py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                data-testid="passkey-prompt-skip"
              >
                {t('auth:passkey.maybeLater')}
              </button>
            </div>

            {/* Benefits List */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('auth:passkey.benefits')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
